import React, { useMemo } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import type { TheoTaxonomyCategory, TheoTaxonomySelection } from "../types/integration";

export interface TheoTaxonomyOption {
  label: string;
  l1: string;
  l2: string | null;
}

export function buildTheoTaxonomyOptions(
  categories: TheoTaxonomyCategory[]
): TheoTaxonomyOption[] {
  return categories.flatMap((category) => [
    { label: category.l1, l1: category.l1, l2: null },
    ...category.l2.map((l2) => ({
      label: `${category.l1} > ${l2}`,
      l1: category.l1,
      l2,
    })),
  ]);
}

export function formatTheoCategorySelectionLabel(
  selection: Pick<TheoTaxonomySelection, "l1" | "l2">
): string {
  const l1 = String(selection.l1 || "").trim();
  const l2 = String(selection.l2 || "").trim();
  if (!l1) {
    return "";
  }
  return l2 ? `${l1} > ${l2}` : l1;
}

interface TheoCategoryAutocompleteProps {
  value: TheoTaxonomySelection[] | undefined;
  onChange: (selections: TheoTaxonomySelection[]) => void;
  options: TheoTaxonomyOption[];
  helperText?: string;
}

const TheoCategoryAutocomplete: React.FC<TheoCategoryAutocompleteProps> = ({
  value,
  onChange,
  options,
  helperText,
}) => {
  const selected = useMemo(() => {
    const selections = Array.isArray(value) ? value : [];
    return selections.flatMap((selection) => {
      const option = options.find(
        (candidate) =>
          candidate.l1 === selection.l1 &&
          (candidate.l2 || null) === (selection.l2 || null)
      );
      return option ? [option] : [];
    });
  }, [options, value]);

  return (
    <Autocomplete
      multiple
      fullWidth
      options={options}
      value={selected}
      onChange={(_event, nextOptions) => {
        onChange(
          nextOptions.map((option) => ({
            l1: option.l1,
            l2: option.l2,
          }))
        );
      }}
      getOptionLabel={(option) => option.label}
      getOptionKey={(option) => JSON.stringify([option.l1, option.l2])}
      isOptionEqualToValue={(option, nextValue) =>
        option.l1 === nextValue.l1 && option.l2 === nextValue.l2
      }
      autoHighlight
      filterSelectedOptions
      renderInput={(params) => (
        <TextField
          {...params}
          label="Theo category"
          placeholder="Search L1 or L2 categories"
          helperText={helperText}
          size="small"
        />
      )}
    />
  );
};

export default TheoCategoryAutocomplete;
